import React, { useState, useEffect } from 'react';
import {
  Shield, Plus, Edit2, Trash2, RefreshCw, ChevronDown, ChevronRight,
  AlertCircle, Check, X, Search, Filter, Copy, Zap, FileText, Save
} from 'lucide-react';

const GoldenStandardsManager = ({ api, tenantId, tenantName, onBack }) => {
  const [standards, setStandards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingStandard, setEditingStandard] = useState(null);
  const [formData, setFormData] = useState({
    control_id: '',
    category: '',
    title: '',
    description: '',
    classification: 'Technical',
    implementation_guidance: '',
    related_frameworks: [],
    risk_level: 'High',
    automation_possible: false,
    keywords: []
  });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Available options
  const classifications = ['Foundational', 'Technical', 'Process', 'Governance'];
  const riskLevels = [
    { value: 'Critical', label: 'Critical', color: '#dc2626' },
    { value: 'High', label: 'High', color: '#f59e0b' },
    { value: 'Medium', label: 'Medium', color: '#3b82f6' },
    { value: 'Low', label: 'Low', color: '#22c55e' }
  ];
  const defaultCategories = [
    'AI Governance',
    'Data Risk',
    'Model Risk',
    'System Risk',
    'Operational Risk',
    'Compliance Risk'
  ];

  useEffect(() => {
    loadStandards();
  }, [tenantId, selectedCategory]);

  const loadStandards = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.listTenantGoldenStandards(
        tenantId,
        selectedCategory || null,
        false
      );
      setStandards(response.golden_standards || []);
      setCategories(response.categories || []);

      // Expand all categories by default
      const expanded = {};
      (response.categories || []).forEach(cat => {
        expanded[cat] = true;
      });
      setExpandedCategories(expanded);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeDefaults = async () => {
    if (!window.confirm('Initialize default golden standards for this tenant? This will add any missing default controls.')) {
      return;
    }
    try {
      setLoading(true);
      const response = await api.initializeDefaultGoldenStandards(tenantId);
      alert(`Initialized ${response.created_count} default golden standards`);
      loadStandards();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingStandard(null);
    setFormData({
      control_id: '',
      category: defaultCategories[0],
      title: '',
      description: '',
      classification: 'Technical',
      implementation_guidance: '',
      related_frameworks: [],
      risk_level: 'High',
      automation_possible: false,
      keywords: []
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleEdit = (standard) => {
    setEditingStandard(standard);
    setFormData({
      control_id: standard.control_id,
      category: standard.category,
      title: standard.title,
      description: standard.description,
      classification: standard.classification,
      implementation_guidance: standard.implementation_guidance || '',
      related_frameworks: standard.related_frameworks || [],
      risk_level: standard.risk_level,
      automation_possible: standard.automation_possible,
      keywords: standard.keywords || []
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleDelete = async (standard) => {
    if (!window.confirm(`Delete golden standard "${standard.control_id}: ${standard.title}"?`)) {
      return;
    }
    try {
      await api.deleteGoldenStandard(tenantId, standard.id);
      loadStandards();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);

    try {
      // Validate
      if (!formData.control_id || !formData.title || !formData.description) {
        throw new Error('Control ID, title, and description are required');
      }

      if (editingStandard) {
        await api.updateGoldenStandard(tenantId, editingStandard.id, formData);
      } else {
        await api.createGoldenStandard(tenantId, formData);
      }

      setShowForm(false);
      loadStandards();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getRiskLevelColor = (level) => {
    const found = riskLevels.find(r => r.value === level);
    return found ? found.color : '#6b7280';
  };

  // Filter standards
  const filteredStandards = standards.filter(s => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        s.control_id.toLowerCase().includes(query) ||
        s.title.toLowerCase().includes(query) ||
        s.description.toLowerCase().includes(query) ||
        (s.keywords && s.keywords.some(k => k.toLowerCase().includes(query)))
      );
    }
    return true;
  });

  // Group by category
  const groupedStandards = filteredStandards.reduce((acc, standard) => {
    const cat = standard.category;
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(standard);
    return acc;
  }, {});

  if (loading && standards.length === 0) {
    return (
      <div className="golden-standards-manager">
        <div className="admin-loading">
          <RefreshCw className="spinning" size={32} />
          <p>Loading golden standards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="golden-standards-manager">
      {/* Header */}
      <div className="gs-header">
        <div className="gs-header-left">
          <button onClick={onBack} className="admin-btn admin-btn-ghost">
            <ChevronRight size={18} style={{ transform: 'rotate(180deg)' }} />
            Back
          </button>
          <Shield size={28} color="#667eea" />
          <div>
            <h2>Golden Standards</h2>
            <p>Manage golden controls for {tenantName}</p>
          </div>
        </div>
        <div className="gs-header-actions">
          <button
            onClick={loadStandards}
            className="admin-btn admin-btn-secondary"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
          <button
            onClick={handleInitializeDefaults}
            className="admin-btn admin-btn-secondary"
          >
            <Copy size={16} />
            Initialize Defaults
          </button>
          <button
            onClick={handleCreateNew}
            className="admin-btn admin-btn-primary"
          >
            <Plus size={16} />
            Add Control
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="gs-error">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={loadStandards}>Retry</button>
        </div>
      )}

      {/* Stats */}
      <div className="gs-stats">
        <div className="gs-stat">
          <span className="gs-stat-number">{standards.length}</span>
          <span className="gs-stat-label">Total Controls</span>
        </div>
        <div className="gs-stat">
          <span className="gs-stat-number">{categories.length}</span>
          <span className="gs-stat-label">Categories</span>
        </div>
        <div className="gs-stat">
          <span className="gs-stat-number">{standards.filter(s => s.risk_level === 'Critical').length}</span>
          <span className="gs-stat-label">Critical</span>
        </div>
        <div className="gs-stat">
          <span className="gs-stat-number">{standards.filter(s => s.automation_possible).length}</span>
          <span className="gs-stat-label">Automatable</span>
        </div>
      </div>

      {/* Filters */}
      <div className="gs-filters">
        <div className="gs-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search controls..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="gs-filter">
          <Filter size={18} />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {defaultCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Standards List */}
      <div className="gs-list">
        {Object.keys(groupedStandards).length === 0 ? (
          <div className="gs-empty">
            <Shield size={48} color="#9ca3af" />
            <h3>No Golden Standards</h3>
            <p>Click "Initialize Defaults" to add the standard set of controls, or create custom controls.</p>
          </div>
        ) : (
          Object.entries(groupedStandards)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, categoryStandards]) => (
              <div key={category} className="gs-category">
                <div
                  className="gs-category-header"
                  onClick={() => toggleCategory(category)}
                >
                  {expandedCategories[category] ? (
                    <ChevronDown size={18} />
                  ) : (
                    <ChevronRight size={18} />
                  )}
                  <span className="gs-category-name">{category}</span>
                  <span className="gs-category-count">{categoryStandards.length}</span>
                </div>

                {expandedCategories[category] && (
                  <div className="gs-category-items">
                    {categoryStandards
                      .sort((a, b) => a.control_id.localeCompare(b.control_id))
                      .map(standard => (
                        <div key={standard.id} className="gs-item">
                          <div className="gs-item-header">
                            <span className="gs-item-id">{standard.control_id}</span>
                            <span
                              className="gs-item-risk"
                              style={{ backgroundColor: getRiskLevelColor(standard.risk_level) }}
                            >
                              {standard.risk_level}
                            </span>
                            {standard.automation_possible && (
                              <span className="gs-item-auto" title="Automation possible">
                                <Zap size={14} />
                              </span>
                            )}
                            {standard.is_default && (
                              <span className="gs-item-default" title="Default control">
                                <FileText size={14} />
                              </span>
                            )}
                          </div>
                          <div className="gs-item-title">{standard.title}</div>
                          <div className="gs-item-description">{standard.description}</div>
                          {standard.keywords && standard.keywords.length > 0 && (
                            <div className="gs-item-keywords">
                              {standard.keywords.map((kw, i) => (
                                <span key={i} className="gs-keyword">{kw}</span>
                              ))}
                            </div>
                          )}
                          <div className="gs-item-actions">
                            <button
                              onClick={() => handleEdit(standard)}
                              className="gs-item-btn"
                              title="Edit"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDelete(standard)}
                              className="gs-item-btn gs-item-btn-danger"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="gs-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gs-modal-header">
              <h3>{editingStandard ? 'Edit Golden Standard' : 'Create Golden Standard'}</h3>
              <button onClick={() => setShowForm(false)} className="gs-modal-close">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="gs-form">
              {formError && (
                <div className="gs-form-error">
                  <AlertCircle size={16} />
                  <span>{formError}</span>
                </div>
              )}

              <div className="gs-form-row">
                <div className="gs-form-group">
                  <label>Control ID *</label>
                  <input
                    type="text"
                    value={formData.control_id}
                    onChange={(e) => setFormData({ ...formData, control_id: e.target.value })}
                    placeholder="e.g., AI-001, CUSTOM-001"
                    required
                  />
                </div>
                <div className="gs-form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {defaultCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="gs-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Control title"
                  required
                />
              </div>

              <div className="gs-form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of the control"
                  rows={3}
                  required
                />
              </div>

              <div className="gs-form-row">
                <div className="gs-form-group">
                  <label>Classification</label>
                  <select
                    value={formData.classification}
                    onChange={(e) => setFormData({ ...formData, classification: e.target.value })}
                  >
                    {classifications.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="gs-form-group">
                  <label>Risk Level</label>
                  <select
                    value={formData.risk_level}
                    onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                  >
                    {riskLevels.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="gs-form-group">
                <label>Implementation Guidance</label>
                <textarea
                  value={formData.implementation_guidance}
                  onChange={(e) => setFormData({ ...formData, implementation_guidance: e.target.value })}
                  placeholder="How to implement this control"
                  rows={2}
                />
              </div>

              <div className="gs-form-group">
                <label>Related Frameworks (comma-separated)</label>
                <input
                  type="text"
                  value={formData.related_frameworks.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData,
                    related_frameworks: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                  })}
                  placeholder="e.g., NIST AI RMF, ISO 42001, EU AI Act"
                />
              </div>

              <div className="gs-form-group">
                <label>Keywords (comma-separated)</label>
                <input
                  type="text"
                  value={formData.keywords.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData,
                    keywords: e.target.value.split(',').map(s => s.trim().toLowerCase()).filter(s => s)
                  })}
                  placeholder="e.g., ai, model, governance, risk"
                />
                <small>Keywords are used to automatically map threats to this control</small>
              </div>

              <div className="gs-form-group gs-form-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.automation_possible}
                    onChange={(e) => setFormData({ ...formData, automation_possible: e.target.checked })}
                  />
                  <span>Automation possible</span>
                </label>
              </div>

              <div className="gs-form-actions">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="admin-btn admin-btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn admin-btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {editingStandard ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoldenStandardsManager;
